<script setup lang="ts">
import type { PrimitiveProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import type { ButtonVariants } from '.'
import { Primitive } from 'reka-ui'
import { cn } from '@/lib/ui/utils'
import { buttonVariants } from '.'

interface Props extends PrimitiveProps {
  variant?: ButtonVariants['variant']
  size?: ButtonVariants['size']
  class?: HTMLAttributes['class']
}

const props = withDefaults(defineProps<Props>(), {
  as: 'button',
})
</script>

<template>
  <Primitive
    data-slot="button"
    :as="as"
    :as-child="asChild"
    :class="cn(buttonVariants({ variant, size }), props.class)"
  >
    <slot />
  </Primitive>
</template>

<style scoped>
/* Enhanced button hover and active effects using specific button classes */
.inline-flex.items-center.justify-center:hover {
  transform: translateY(-1px);
  transition: all 0.2s ease;
}

.inline-flex.items-center.justify-center:active {
  transform: translateY(0px) scale(0.98);
  transition: all 0.1s ease;
}

/* Primary buttons - brand color hover effects */
.bg-primary:hover {
  background-color: hsl(var(--primary) / 0.9);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.bg-primary:active {
  background-color: hsl(var(--primary) / 0.8);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Secondary buttons - light brand color background */
.bg-secondary:hover {
  background-color: hsl(var(--primary) / 0.1);
  color: hsl(var(--primary));
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.bg-secondary:active {
  background-color: hsl(var(--primary) / 0.15);
  color: hsl(var(--primary));
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Ghost buttons - light brand color background with brand color text */
button:not(.bg-primary):not(.bg-secondary):not(.border):hover {
  background-color: hsl(var(--primary) / 0.1);
  color: hsl(var(--primary));
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
}

button:not(.bg-primary):not(.bg-secondary):not(.border):active {
  background-color: hsl(var(--primary) / 0.15);
  color: hsl(var(--primary));
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

/* Outline buttons - light brand color background with brand color text */
.border.bg-background:hover {
  background-color: hsl(var(--primary) / 0.1);
  color: hsl(var(--primary));
  border-color: hsl(var(--primary) / 0.3);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.border.bg-background:active {
  background-color: hsl(var(--primary) / 0.15);
  color: hsl(var(--primary));
  border-color: hsl(var(--primary) / 0.4);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Link buttons - brand color with opacity changes */
.text-primary.underline-offset-4:hover {
  color: hsl(var(--primary) / 0.8);
}

.text-primary.underline-offset-4:active {
  color: hsl(var(--primary) / 0.7);
}
</style>
